import React, { useEffect, useState } from "react";
import { Loader2, TrendingUp, MousePointerClick, Heart, MessageCircle, Wallet, Truck, ShoppingBag, Wand2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const rupees = (p) => `₹${((p || 0) / 100).toLocaleString("en-IN")}`;

export default function Analytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api.get("/analytics/me").then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (!user) return <div className="p-12 text-center text-stone-500">Sign in to view your analytics.</div>;
  if (loading) return <div className="p-12 text-center"><Loader2 className="inline animate-spin" /></div>;
  if (!data) return <div className="p-12 text-center text-stone-500">No data yet.</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-4" data-testid="analytics-page">
      <div className="flex items-center gap-2 mb-1"><TrendingUp className="text-fuchsia-500" size={22} />
        <h1 className="heading text-2xl font-black text-stone-900">Analytics</h1></div>
      <p className="text-xs text-stone-500 mb-4">Your Find2Near activity at a glance.</p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card icon={Heart}         color="from-rose-500 to-pink-700"     label="Total likes"      value={data.feed.likes} />
        <Card icon={MessageCircle} color="from-sky-500 to-blue-700"      label="Comments"         value={data.feed.comments} />
        <Card icon={TrendingUp}    color="from-emerald-500 to-teal-700"  label="Engagement rate"  value={data.feed.engagement_rate} suffix="× per post" />

        <Card icon={Wand2}         color="from-fuchsia-500 to-purple-700" label="AutoListings"     value={`${data.autolistings.published}/${data.autolistings.total}`} suffix="published" />
        <Card icon={MousePointerClick} color="from-amber-500 to-orange-700" label="Share clicks"   value={data.autolistings.share_clicks} suffix={`${data.autolistings.ctr_per_post} per post`} />

        <Card icon={Wallet}        color="from-stone-700 to-stone-900"   label="Micro-spend"      value={rupees(data.wallet_spend.total_paise)} />
        <Card icon={Truck}         color="from-orange-500 to-red-700"    label="Deliveries done"  value={data.delivery.completed} suffix={`${data.delivery.avg_minutes || 0}m avg`} />
        <Card icon={ShoppingBag}   color="from-lime-500 to-emerald-700"  label="Orders"           value={`${data.commerce.orders_paid}/${data.commerce.orders}`} suffix="paid" />
      </div>

      {Object.keys(data.wallet_spend.by_service || {}).length > 0 && (
        <div className="mt-4 bg-white border border-stone-200 rounded-2xl p-4">
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2">Micro-spend by service</div>
          <div className="grid grid-cols-3 gap-2 text-center text-sm">
            {Object.entries(data.wallet_spend.by_service).map(([svc, m]) => (
              <div key={svc} className="bg-stone-50 rounded-lg p-2">
                <div className="text-[10px] uppercase font-bold text-stone-500">{svc}</div>
                <div className="font-black text-[#E05E36]">{rupees(m.total_paise)}</div>
                <div className="text-[10px] text-stone-500">{m.count}x</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 text-[10px] text-center text-stone-400">Snapshot @ {new Date(data.generated_at).toLocaleString()}</div>
    </div>
  );
}

function Card({ icon: Icon, color, label, value, suffix }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4">
      <div className={`w-10 h-10 rounded-2xl grid place-items-center text-white bg-gradient-to-br ${color}`}><Icon size={16} /></div>
      <div className="mt-3 text-2xl font-black text-stone-900">{value}</div>
      <div className="text-xs text-stone-500">{label}{suffix ? <span> · {suffix}</span> : null}</div>
    </div>
  );
}
